(function () {
  class ZenIngredientCards extends HTMLElement {
    constructor() {
      super();
      this._onCardClick = this._onCardClick.bind(this);
    }

    connectedCallback() {
      const modalId = this.getAttribute('data-modal-id');
      if (!modalId) return;
      this._modal = document.getElementById(modalId);
      if (!this._modal) return;

      this._cards = Array.from(this.querySelectorAll('.zen-ki-card'));
      this._cards.forEach((card) => {
        const trigger = card.querySelector('.zen-ki-card__toggle');
        if (trigger) trigger.addEventListener('click', this._onCardClick);
      });

      this._setupModal();
    }

    disconnectedCallback() {
      if (this._cards) {
        this._cards.forEach((card) => {
          const trigger = card.querySelector('.zen-ki-card__toggle');
          if (trigger) trigger.removeEventListener('click', this._onCardClick);
        });
      }
    }

    _onCardClick(event) {
      const card = event.currentTarget.closest('.zen-ki-card');
      if (!card) return;
      const index = parseInt(card.dataset.index, 10) || 0;
      this._openModal(index);
    }

    _setupModal() {
      const modal = this._modal;
      this._slider = modal.querySelector('.zen-ki-modal__slider');
      this._slides = Array.from(modal.querySelectorAll('.zen-ki-slide'));
      this._dotsContainer = modal.querySelector('.zen-ki-modal__dots');
      this._countCurrent = modal.querySelector('.zen-ki-modal__count-current');
      this._countTotal = modal.querySelector('.zen-ki-modal__count-total');
      this._dialog = modal.querySelector('.zen-ki-modal__dialog');

      // Build dots
      if (this._dotsContainer && this._slides.length > 0) {
        this._dotsContainer.innerHTML = '';
        this._slides.forEach((_, i) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'zen-ki-modal__dot';
          dot.setAttribute('role', 'tab');
          dot.setAttribute('aria-label', 'Go to ingredient ' + (i + 1));
          if (i === 0) dot.classList.add('is-active');
          dot.addEventListener('click', () => this._goToSlide(i));
          this._dotsContainer.appendChild(dot);
        });
      }

      // Update total count
      if (this._countTotal) {
        this._countTotal.textContent = this._pad2(this._slides.length);
      }

      // Close handlers
      modal.querySelectorAll('[data-zen-ki-close]').forEach((el) => {
        el.addEventListener('click', () => this._closeModal());
      });

      // Esc key
      this._onKeyDown = (e) => {
        if (e.key === 'Escape' && modal.classList.contains('zen-ki-modal--open')) {
          this._closeModal();
        }
      };
      document.addEventListener('keydown', this._onKeyDown);

      // Slider scroll → update active dot/count
      if (this._slider) {
        let rafId = null;
        this._slider.addEventListener(
          'scroll',
          () => {
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => this._updateActive());
          },
          { passive: true }
        );
      }
    }

    _openModal(index) {
      const modal = this._modal;
      if (!modal) return;
      modal.hidden = false;
      // Force reflow before adding open class for the transition
      void modal.offsetHeight;
      modal.classList.add('zen-ki-modal--open');
      document.body.classList.add('zen-ki-modal-open');

      // Jump to the correct slide without animation, then update state
      requestAnimationFrame(() => {
        this._goToSlide(index, false);
        if (this._dialog) this._dialog.focus({ preventScroll: true });
      });
    }

    _closeModal() {
      const modal = this._modal;
      if (!modal) return;
      modal.classList.remove('zen-ki-modal--open');
      document.body.classList.remove('zen-ki-modal-open');
      // Hide after transition completes
      setTimeout(() => {
        modal.hidden = true;
      }, 300);
    }

    _goToSlide(index, smooth) {
      if (!this._slider || !this._slides[index]) return;
      const target = this._slides[index];
      const offset = target.offsetLeft - this._slider.offsetLeft;
      this._slider.scrollTo({
        left: offset,
        behavior: smooth === false ? 'auto' : 'smooth',
      });
      // Optimistically update active state immediately for snappy UI
      this._setActive(index);
    }

    _updateActive() {
      if (!this._slider) return;
      const center = this._slider.scrollLeft + this._slider.clientWidth / 2;
      let closestIdx = 0;
      let closestDist = Infinity;
      this._slides.forEach((slide, i) => {
        const slideCenter =
          slide.offsetLeft - this._slider.offsetLeft + slide.offsetWidth / 2;
        const dist = Math.abs(slideCenter - center);
        if (dist < closestDist) {
          closestDist = dist;
          closestIdx = i;
        }
      });
      this._setActive(closestIdx);
    }

    _setActive(index) {
      if (this._dotsContainer) {
        this._dotsContainer
          .querySelectorAll('.zen-ki-modal__dot')
          .forEach((d, i) => d.classList.toggle('is-active', i === index));
      }
      if (this._countCurrent) {
        this._countCurrent.textContent = this._pad2(index + 1);
      }
    }

    _pad2(n) {
      return String(n).padStart(2, '0');
    }
  }

  if (!customElements.get('zen-ingredient-cards')) {
    customElements.define('zen-ingredient-cards', ZenIngredientCards);
  }
})();
